/**
 * Session + bootstrap types.
 *
 * Mirrors the BFF's ``/api/me/bootstrap`` response shape from
 * ``meridian_bff._bootstrap`` (BFF PR C, commit C7). When the
 * BFF surface evolves (new roles, new preference fields), the
 * canonical types come from
 * ``ui/src/lib/api-types.gen.ts`` (openapi-typescript codegen);
 * the types declared here are a hand-written subset for the
 * fields the chrome + auth flow consumes directly.
 *
 * The deployment designation (``a`` / ``b`` / ``c``) is **not** in the
 * bootstrap response — it's a deployment-environment property
 * surfaced via ``window.__MERIDIAN_NETWORK__``, populated by the
 * UI's nginx runtime (``ui/nginx.conf`` template) per ADR-002.
 * The :func:`useDeploymentNetwork` hook resolves it at runtime.
 *
 * Cross-references:
 *   - REQ-AUTH-024..027 (role vocabulary).
 *   - REQ-AUTH-057 / 062 (bootstrap payload shape).
 *   - REQ-AUTH-065 (bootstrap carries the visible-Profiles list —
 *     ADR-033 §4.1; same shape as ``GET /api/profiles``).
 *   - REQ-UI-044 (preferences applied before first paint).
 */
import type { components } from "@/lib/api-types.gen";

export type Role = "operator" | "analyst" | "admin" | "support_engineer";
/**
 * The deployment designation this session is served from (ADR-042 D-1).
 *
 * Neutral designations, not classification labels: what the deployment is
 * *marked* as is its own configuration (see ``ClassificationStyle`` and the
 * badge caption), never a consequence of which designation it holds.
 */
export type Network = "a" | "b" | "c";
export type ThemeMode = "light" | "dark" | "system";

/**
 * The Profile document (``profile_schema_version: 1``) — the canonical
 * UI-configuration bundle (ADR-033 §3.1). Identical in shape whether it
 * lives in a saved Profile (``auth.profiles.document``) or as the live
 * working state (``PreferencesV3.working_state``). Re-exported from the
 * generated **output** variant (optional fields populated by the BFF on
 * read) so the UI and the wire stay in lock-step without a hand-rolled
 * mirror (CLAUDE.md §5.11 — generated types only).
 *
 * Cross-references:
 *   - REQ-AUTH-070 (preferences v3 — working_state is a Profile document).
 *   - ADR-033 §3.1.
 */
export type ProfileDocument = components["schemas"]["ProfileDocumentV1-Output"];

/**
 * Spatial-unit + coordinate-format slice of a Profile document
 * (ADR-033 §7). Canonical data is stored SI / decimal degrees; the UI
 * converts at render via :func:`formatDistance` / :func:`formatCoordinate`.
 * Re-exported from the generated schema so the units subsystem and the wire
 * stay in lock-step without a hand-rolled mirror (CLAUDE.md §5.11 —
 * generated types only).
 *
 * Cross-references:
 *   - REQ-UI-083 (distance units) / REQ-UI-084 (coordinate format).
 *   - ADR-033 §7.
 */
export type UnitsSettings = components["schemas"]["UnitsSettings"];

/** The distance-unit enum (``nautical_miles`` | ``kilometers`` |
 *  ``statute_miles``) — the input to :func:`formatDistance`. REQ-UI-083. */
export type DistanceUnit = UnitsSettings["distance"];

/** The coordinate-format enum (``decimal_degrees`` | ``dms`` | ``mgrs``) —
 *  the input to :func:`formatCoordinate`. REQ-UI-084. */
export type CoordinateFormat = UnitsSettings["coordinate_format"];

/**
 * The ``map`` sub-document of a Profile (ADR-033 §9) — viewport home,
 * toggleable-layer defaults, per-marker appearance, and the (POST-MVP)
 * tile/basemap selectors. Re-exported from the generated **output** variant
 * (optional fields populated by the BFF on read) so the map subsystem and
 * the wire stay in lock-step without a hand-rolled mirror (CLAUDE.md §5.11 —
 * generated types only).
 *
 * Cross-references:
 *   - REQ-UI-083 (scale unit) / REQ-UI-089 (geometry defaults) /
 *     REQ-UI-090 (nav-home); ADR-033 §9.
 */
export type MapSettings = components["schemas"]["MapSettings-Output"];

/**
 * The Profile's home viewport — the nav-home target (ADR-033 §9.4): a
 * ``{ center: { lon, lat }, zoom }`` triple in WGS-84 decimal degrees. The
 * map converts ``center`` to MapLibre's ``[lon, lat]`` tuple at the consuming
 * site. Re-exported from the generated schema (CLAUDE.md §5.11).
 *
 * Cross-references:
 *   - REQ-UI-090 (nav-home → Profile home); ADR-033 §9.4.
 */
export type MapHome = components["schemas"]["MapHome"];

/**
 * One top-bar clock (ADR-033 §8). ``timezone`` is an IANA tz-database name
 * (e.g. ``"America/Denver"``) or ``"UTC"``; the UI renders via
 * ``Intl.DateTimeFormat`` (DST-correct, no server tz database needed).
 * Re-exported from the generated schema (CLAUDE.md §5.11 — generated types
 * only).
 *
 * Cross-references:
 *   - REQ-UI-085 (top-bar clocks); ADR-033 §8.
 */
export type ClockSettings = components["schemas"]["ClockSettings"];

/**
 * Per-panel sort selection for the operator list panels (REQ-UI-110) — the
 * ``list_sort`` sub-document of a Profile. Each panel's selection is optional;
 * an absent panel (``undefined`` / ``null``) means that panel uses its
 * built-in default order (Platforms: display name asc; Detections / Alerts:
 * time desc). Persisted in the working state + saved Profiles like every other
 * preference, so an operator's sort choices travel across sessions. The BFF
 * stores + round-trips the selection; the field semantics (and the
 * ``platform_name`` producer-name join on Detections / Alerts) live in the UI.
 * Re-exported from the generated **output** variant so the sort menu + the wire
 * stay in lock-step without a hand-rolled mirror (CLAUDE.md §5.11 — generated
 * types only).
 *
 * Cross-references:
 *   - REQ-UI-110 (operator list sort); ADR-022 / ADR-024 / ADR-033.
 */
export type ListSortSettings = components["schemas"]["ListSortSettings"];

/**
 * The Platforms-panel sort selection (REQ-UI-110): a ``{ field, direction }``
 * where ``field`` is one of ``display_name`` | ``health`` | ``last_status_time``
 * | ``detection_count`` and ``direction`` is ``asc`` | ``desc``. ``health``
 * sorts by the operator severity order (offline → degraded → unknown →
 * healthy), not the raw enum int. Re-exported from the generated schema so the
 * sort menu's field union stays anchored on the BFF enum (CLAUDE.md §5.11).
 */
export type PlatformListSort = components["schemas"]["PlatformListSort"];

/**
 * The Detections-panel sort selection (REQ-UI-110): ``field`` is one of
 * ``time`` | ``classification`` | ``platform_name`` | ``confidence``.
 * ``platform_name`` sorts by the detecting platform's display name (joined
 * client-side from the producers inventory; ``classification`` / ``confidence``
 * are nullable and sort nulls-last). Re-exported from the generated schema
 * (CLAUDE.md §5.11).
 */
export type DetectionListSort = components["schemas"]["DetectionListSort"];

/**
 * The Alerts-panel sort selection (REQ-UI-110): ``field`` is one of ``time``
 * (``received_at``) | ``priority`` | ``alert_type`` | ``platform_name``.
 * ``priority`` / ``alert_type`` sort by the SAPIENT enum int (higher priority
 * first when ``desc``); ``platform_name`` by the joined source-platform name.
 * Re-exported from the generated schema (CLAUDE.md §5.11).
 */
export type AlertListSort = components["schemas"]["AlertListSort"];
/** The Tracks-panel sort selection (ADR-041; REQ-UI-170). */
export type TrackListSort = components["schemas"]["TrackListSort"];

/**
 * The active-Profile pointer carried in the working state (ADR-033 §3.2):
 * the Profile most-recently applied plus a content hash so the UI can
 * render "Active: «Name»" and a "(modified)" marker once the working
 * state diverges. ``null`` ⇒ the working state is unnamed / custom.
 *
 * Cross-references:
 *   - REQ-UI-074 / REQ-UI-075 (Active + Apply); ADR-033 §3.2.
 */
export type ActiveProfileRef = components["schemas"]["ActiveProfileRef"];

/**
 * The user's selected **login-default** Profile (ADR-033 amended 2026-06-08):
 * a ``{ scope, profile_id, name }`` pointer the UI resolves at login (loads it
 * into the working state, falling back to the built-in Shared default when it
 * is unset or the referenced Profile no longer exists). Distinct from
 * :type:`ActiveProfileRef` (the most-recently-applied Profile *this session*);
 * the BFF only round-trips this pointer. Re-exported from the generated schema
 * so the "Set as default" affordance and the wire stay in lock-step (CLAUDE.md
 * §5.11 — generated types only).
 *
 * Cross-references:
 *   - REQ-UI-074 / REQ-UI-075 (Profiles surface); ADR-033 §3.2.
 */
export type DefaultProfileRef = components["schemas"]["DefaultProfileRef"];

/**
 * The user-preferences document — **schema version 3** (ADR-033 §3.2,
 * REQ-AUTH-070). The live UI configuration is the ``working_state`` (a
 * full :type:`ProfileDocument`); ``active_profile`` points at the Profile
 * it was last applied from (``null`` = unnamed / custom).
 *
 * Permissive + forward-compatible by design: ``schema_version`` and both
 * payload fields are optional so a bootstrap that returns ``{}`` (no
 * ``auth.user_preferences`` row — a brand-new user) and an older v1/v2
 * document the BFF forward-migrates both deserialise without throwing.
 * The index signature preserves unknown fields on round-trip per the
 * REQ-AUTH-060 discipline.
 */
export interface UserPreferences {
  schema_version?: 3;
  /** The live, applied UI configuration — a full Profile document. */
  working_state?: ProfileDocument;
  /** Pointer to the most-recently-applied Profile (``null`` = custom). */
  active_profile?: ActiveProfileRef | null;
  /** The user's selected login-default Profile (``null`` = none — the UI
   *  falls back to the built-in Shared default at login). Round-tripped on
   *  every PUT so a working-state edit never drops it (ADR-033 §3.2). */
  default_profile?: DefaultProfileRef | null;
  // Unknown fields preserved opaquely per REQ-AUTH-060 round-trip
  // discipline (and so a v1/v2 document the BFF hasn't yet migrated
  // forward survives a read here without a type error).
  [key: string]: unknown;
}

export interface SessionUser {
  subject_id: string;
  username: string;
  email: string | null;
  roles: readonly Role[];
}

/**
 * Metadata projection of a named Profile (no document). The
 * canonical shape is the BFF's ``ProfileSummary`` schema; we
 * re-export the generated type so the UI and the wire stay in
 * lock-step without a hand-maintained mirror (CLAUDE.md §5.11 —
 * generated types only). Served by ``GET /api/profiles`` and
 * carried in the bootstrap ``profiles`` field.
 *
 * Cross-references:
 *   - REQ-AUTH-065 (bootstrap + list shape); ADR-033 §4.1.
 */
export type ProfileSummary = components["schemas"]["ProfileSummary"];

/**
 * Full Profile — metadata plus the forward-migrated document.
 * Re-exports the generated ``ProfileDetail`` (output variant of
 * the document, so optional fields are populated). Returned by
 * ``GET /api/profiles/{id}``.
 *
 * Cross-references:
 *   - REQ-AUTH-066 (single-Profile read); ADR-033 §3.1 / §4.1.
 */
export type ProfileDetail = components["schemas"]["ProfileDetail"];

export interface BootstrapResponse {
  user: SessionUser;
  preferences: UserPreferences;
  /**
   * Visible Profiles for the caller (all Global + the caller's
   * User Profiles), metadata only — the same list as
   * ``GET /api/profiles``. Seeds the gear-menu Profiles submenu
   * on first paint without a second round-trip (REQ-AUTH-065;
   * ADR-033 §4.1). Optional on the wire for resilience against a
   * BFF that predates the field; the provider falls back to an
   * empty list + a ``refresh()`` fetch.
   */
  profiles?: ProfileSummary[];
}

export interface SessionContextValue {
  user: SessionUser;
  network: Network;
  hasRole: (role: Role) => boolean;
  hasAnyRole: (roles: readonly Role[]) => boolean;
}

/**
 * Read the deployment-environment network identifier.
 *
 * The deployment-environment automation populates
 * ``window.__MERIDIAN_NETWORK__`` (via the nginx server's
 * ``sub_filter`` directive or via an injected ``<meta>`` tag at
 * build-time per ADR-019). Defaults to ``"u"`` if absent — the
 * test fixtures + local dev environment run against deployment ``a``, the
 * default designation (ADR-042 D-1).
 */
export function readDeploymentNetwork(): Network {
  if (typeof window === "undefined") {
    return "a";
  }
  const declared = (window as { __MERIDIAN_NETWORK__?: string }).__MERIDIAN_NETWORK__;
  if (declared === "a" || declared === "b" || declared === "c") {
    return declared;
  }
  return "a";
}
